import { fraction, fromNet, rateFor, roundCents, type TaxRate, toCents, toNet, toNumber, toStored } from "../../shared/money";
import type { GroupTaxBasis, ItemPricing, LabourUnit, PriceListData, PriceListSettings, PricingRule } from "./meta";

/*
 * The price formula, shared by the server (what gets saved to ServiceM8) and the screens (the
 * breakdown on an item), so the two always agree. No server or browser imports.
 *
 * Worked out before tax, then put in the item's basis:
 *   cost + markup (% of the cost, or a fixed amount)   - or a typed base price ("manual")
 *   + labour (minutes at the labour rate)
 * then rounded as the customer sees it: an item priced with tax included rounds the tax-included
 * price ("$49.95 inc GST"), one priced without tax rounds that ("$49.99", tax added on invoices).
 *
 * `tax` is always the item's own rate as a fraction (0.1 for 10%; see shared/money.ts rateFor).
 *
 * An item in a group is taxed as the group says (its rate, with or without tax): one that
 * isn't yet is priced as it will be once it is (asGroupTaxed), and updating it sets both.
 */

export interface Basis {
    /** Whether the item's price and cost include tax. */
    includesTax: boolean;
    /** The item's tax rate as a fraction. */
    tax: number;
}

export interface PriceBreakdown {
    mode: PricingRule["mode"];
    /** Before tax. */
    costNet: number;
    markupNet: number;
    baseNet: number;
    labourNet: number;
    labourMinutes: number;
    totalNet: number;
    /** The total in the item's basis, before and after rounding. */
    total: number;
    rounded: number;
    /** The item's price, as ServiceM8 stores it (item's basis, to the cent). */
    price: string;
}

const basisOf = (item: { price_includes_taxes: boolean }, tax: number): Basis => ({ includesTax: item.price_includes_taxes, tax });

/** An item's tax rate: its own, else the account's default (its uuid, blank without any), and that as a fraction. */
export interface ItemRate {
    uuid: string;
    tax: number;
}

/*
 * An item's ServiceM8 cost is its part's own cost plus what its labour time costs the business (its
 * pricing's labour time at the labour cost: labourCostOf). Each cost this app saves says how much
 * labour went in (labourInCost), so the part's is always ServiceM8's less that (partItem), even after
 * someone changes the cost in ServiceM8. Everything worked out from a cost (its pricing, what it
 * makes, a kit's parts) uses the part's; ServiceM8 gets it with its labour now (costWithLabour). When
 * its labour costs another amount now, its cost shows as a cost to update (labourChanged).
 */

/** What an item's part cost needs: the labour in each item's ServiceM8 cost (before tax), and the tax rates. */
export type PartCostData = Pick<PriceListData, "labourInCost" | "taxRates">;
/** And for its ServiceM8 cost: its labour time (its pricing's), and the labour cost. */
export type CostData = PartCostData & Pick<PriceListData, "itemPricing"> & { settings: Pick<PriceListSettings, "labourCost"> };

type CostedItem = { uuid: string; cost: string; price_includes_taxes: boolean; tax_rate_uuid: string };

/**
 * A labour amount before tax, in an item's basis (with its tax when its prices include it, as ServiceM8 keeps its cost), to the
 * cent: added to a part cost and taken off again it's the same amount, so the part comes back as it was (unrounded, $18.975 of
 * labour would round up going in, the part down coming out, a cent more each time).
 */
const inBasis = (net: number, item: Omit<CostedItem, "cost" | "uuid">, data: Pick<PartCostData, "taxRates">) =>
    net > 0 ? roundCents(fromNet(net, item.price_includes_taxes, fraction(rateFor(item.tax_rate_uuid, data.taxRates)))) : 0;

/** The item with its cost as the part's own: ServiceM8's less the labour in it (never below nothing). */
export function partItem<T extends CostedItem>(data: PartCostData, item: T): T {
    const labour = inBasis(toNumber(data.labourInCost[item.uuid] ?? "0"), item, data);
    return labour > 0 ? { ...item, cost: toCents(Math.max(0, toNumber(item.cost) - labour)) } : item;
}

/** Whether a labour cost is set (blank or nothing: not set, so labour's profit can't be told). */
export const hasLabourCost = (settings: Pick<PriceListSettings, "labourCost">) => toNumber(settings.labourCost) > 0;

/** What an item's labour time costs the business, before tax, to the cent: its pricing's minutes at the labour cost. 0: no labour, or no labour cost set. */
export function labourCostOf(data: Pick<CostData, "itemPricing" | "settings">, uuid: string) {
    const minutes = data.itemPricing[uuid]?.labourMinutes ?? 0;
    return minutes > 0 && hasLabourCost(data.settings) ? roundCents((toNumber(data.settings.labourCost) * minutes) / 60) : 0;
}

/** ServiceM8's cost for a part cost (in the item's basis, `item` taxed as it will be): with what its labour costs now. */
export const costWithLabour = (data: CostData, item: Omit<CostedItem, "cost">, partCost: string) => {
    const labour = inBasis(labourCostOf(data, item.uuid), item, data);
    return labour > 0 ? toCents(toNumber(partCost) + labour) : toCents(toNumber(partCost));
};

/**
 * Its part's cost as it will be once its cost to update is done (supplierPrices.ts costUpdates: its supplier price, parts or typed
 * cost as they give it now), in its basis; with none, its part's as it is (partItem). What a cost updated with new labour is built on.
 */
export function estimatedPartCost(data: CostData & Pick<PriceListData, "costUpdates">, item: CostedItem) {
    const target = data.costUpdates[item.uuid];
    if (target === undefined) return partItem(data, item).cost;
    return toCents(Math.max(0, toNumber(target) - toNumber(costWithLabour(data, item, "0"))));
}

/** Its labour costs another amount now than is in its ServiceM8 cost (its labour time or the labour cost changed). */
export const labourChanged = (data: CostData, uuid: string) => Math.abs(labourCostOf(data, uuid) - toNumber(data.labourInCost[uuid] ?? "0")) >= 0.005;

/** Whether an item is taxed as its group says (at no tax, with or without it is the same thing). */
export const taxedAsGroup = (item: { price_includes_taxes: boolean }, rate: ItemRate, group: GroupTaxBasis) =>
    rate.uuid === group.rateUuid && (item.price_includes_taxes === group.includesTax || group.tax === 0);

/** The item taxed as its group says: the same cost before tax, in the group's basis. */
export function asGroupTaxed<T extends { cost: string; price_includes_taxes: boolean }>(item: T, rate: ItemRate, group: GroupTaxBasis) {
    const cost = toCents(fromNet(toNet(toNumber(item.cost), item.price_includes_taxes, rate.tax), group.includesTax, group.tax));
    return { ...item, cost, price_includes_taxes: group.includesTax, tax_rate_uuid: group.rateUuid };
}

/**
 * What its pricing works an item's price out from: the item as it is, or (in a group, taxed
 * otherwise) as it will be once it's taxed as the group says (`taxTo`).
 */
export function pricedAs<T extends { cost: string; price_includes_taxes: boolean }>(item: T, rate: ItemRate, pricing: ItemPricing | undefined) {
    const group = pricing?.groupTax;
    if (!group || taxedAsGroup(item, rate, group)) return { item, tax: rate.tax, taxTo: null };
    return { item: asGroupTaxed(item, rate, group), tax: group.tax, taxTo: group };
}

/** The labour rate per hour, before tax, for an item taxed at `tax`. */
export const labourRateNet = (s: Pick<PriceListSettings, "labourRate" | "labourRateIncludesTax">, tax: number) =>
    toNet(toNumber(s.labourRate), s.labourRateIncludesTax, tax);

function applyRounding(value: number, rule: Pick<PricingRule, "roundMode" | "roundTo">) {
    const step = toNumber(rule.roundTo);
    if (rule.roundMode === "none" || step <= 0) return value;
    // In cents, so float leftovers (0.1 + 0.2) don't push a price over a step.
    const cents = Math.round(value * 100);
    const stepCents = Math.round(step * 100);
    const ratio = cents / stepCents;
    let steps = Math.round(ratio);
    if (rule.roundMode === "up") steps = Math.ceil(ratio - 1e-9);
    if (rule.roundMode === "down") steps = Math.floor(ratio + 1e-9);
    return (steps * stepCents) / 100;
}

/** A plain price: typed in, nothing worked out (manual with no labour or rounding). */
export const isPlain = (r: PricingRule) => r.mode === "manual" && r.labourMinutes <= 0 && (r.roundMode === "none" || toNumber(r.roundTo) <= 0);

/**
 * Pricing worked out from the cost (Cost + % or Cost + amount) with no cost to work from: a $0 cost
 * almost always means one isn't set yet, not a free part, so it gives no price at all (rather than
 * the markup alone, or $0). The item keeps the price it has, flagged, until it has a cost.
 */
export const missingCost = (item: { cost: string }, rule: Pick<PricingRule, "mode"> | undefined) =>
    rule !== undefined && (rule.mode === "percent" || rule.mode === "fixed") && toNumber(item.cost) <= 0;

/** A rule with what a kit's parts' prices add up to (ItemPricing partsPrice), for pricing it from them. */
export type RuleWithParts = PricingRule & { partsPrice?: string | null };

/**
 * The price an item's pricing gives it, or null without any (a plain, typed-in price), with no cost to work from
 * (missingCost), or (a kit priced from its parts' prices) with no parts' prices to work from.
 */
export function calcPrice(
    item: { cost: string; price_includes_taxes: boolean },
    tax: number,
    rule: RuleWithParts | undefined,
    settings: Pick<PriceListSettings, "labourRate" | "labourRateIncludesTax">,
): PriceBreakdown | null {
    if (!rule || missingCost(item, rule)) return null;
    const fromParts = rule.mode === "parts";
    if (fromParts && (rule.partsPrice === null || rule.partsPrice === undefined)) return null;
    const basis = basisOf(item, tax);
    const onCost = rule.mode === "percent" || rule.mode === "fixed";
    const costNet = onCost ? toNet(toNumber(item.cost), basis.includesTax, tax) : 0;
    // What it's worked out from, before tax: a set price's base, a kit's parts' prices; or (on the cost) nothing more.
    let baseNet = 0;
    if (rule.mode === "manual") baseNet = toNumber(rule.basePrice);
    if (fromParts) baseNet = toNumber(rule.partsPrice ?? "0");
    let markupNet = 0;
    if (rule.mode === "percent") markupNet = (costNet * toNumber(rule.markupValue)) / 100;
    if (rule.mode === "fixed") markupNet = toNumber(rule.markupValue);
    if (fromParts) markupNet = (baseNet * toNumber(rule.markupValue)) / 100;
    const labourNet = (labourRateNet(settings, tax) * rule.labourMinutes) / 60;
    const totalNet = costNet + markupNet + baseNet + labourNet;
    const total = fromNet(totalNet, basis.includesTax, tax);
    const rounded = applyRounding(roundCents(total), rule);
    return { mode: rule.mode, costNet, markupNet, baseNet, labourNet, labourMinutes: rule.labourMinutes, totalNet, total, rounded, price: toCents(rounded) };
}

/** A rule amount stored before tax, as shown and typed for the item (its basis). */
export const shownAmount = (stored: string, basis: Basis) => toCents(fromNet(toNumber(stored), basis.includesTax, basis.tax));
/** A typed amount (in the item's basis), as stored: before tax. */
export const storedAmount = (typed: string, basis: Basis) => toStored(toNet(toNumber(typed), basis.includesTax, basis.tax));

/**
 * What an item's pricing adds on top of its cost, exactly (before tax, unrounded): from its rule,
 * or without one, its typed price. For switching between Cost + % and Cost + amount (and back)
 * without losing a cent: the new amount is rounded once, as it's shown.
 */
export function exactMarkup(
    item: { cost: string; price: string; price_includes_taxes: boolean },
    tax: number,
    rule: RuleWithParts | undefined,
    settings: Pick<PriceListSettings, "labourRate" | "labourRateIncludesTax">,
) {
    const costNet = toNet(toNumber(item.cost), item.price_includes_taxes, tax);
    const result = calcPrice(item, tax, rule, settings);
    const totalNet = result ? result.totalNet - result.labourNet : toNet(toNumber(item.price), item.price_includes_taxes, tax);
    return { costNet, markupNet: totalNet - costNet };
}

/** A markup as a percentage of the cost, stored exactly (shown to 2 places: shownPercent). */
export function percentFor(markupNet: number, costNet: number) {
    const pct = costNet > 0 ? (Math.max(0, markupNet) / costNet) * 100 : 0;
    // 8 places: to the cent on any cost (4 would be a cent out on a $10,000 one).
    return String(Math.round(pct * 1e8) / 1e8);
}
/** A markup as an amount, stored exactly before tax, to 8 places like percentFor (shown to the cent: shownAmount). */
export const amountFor = (markupNet: number) => String(Math.round(Math.max(0, markupNet) * 1e8) / 1e8);
/** Labour time as it's shown: in minutes, or hours to 2 places ("" for none). Stored as minutes, exactly. */
export const shownLabour = (minutes: number, unit: LabourUnit) => (minutes ? String(unit === "hours" ? Math.round((minutes / 60) * 100) / 100 : minutes) : "");
/** Typed labour time (in `unit`), as stored: minutes. */
export const storedLabour = (typed: string, unit: LabourUnit) => (typed === "" ? 0 : Number(typed) * (unit === "hours" ? 60 : 1));
/**
 * Whether two rules read the same on screen: the fields their way of pricing uses, each as it's shown
 * (money to the cent in `basis`, a % to a millionth, labour in its unit). Fields it doesn't use (a
 * base price left over from a switch, a rounding step with no rounding) and tiny differences from
 * working a value out again don't count: an edit undone is no edit.
 */
export function sameRuleShown(a: PricingRule, b: PricingRule, basis: Basis) {
    if (a.mode !== b.mode || a.labourUnit !== b.labourUnit || a.roundMode !== b.roundMode) return false;
    if (a.roundMode !== "none" && toNumber(a.roundTo) !== toNumber(b.roundTo)) return false;
    if (shownLabour(a.labourMinutes, a.labourUnit) !== shownLabour(b.labourMinutes, b.labourUnit)) return false;
    if (a.mode === "percent") return Math.abs(toNumber(a.markupValue) - toNumber(b.markupValue)) < 1e-6;
    const field = a.mode === "fixed" ? "markupValue" : "basePrice";
    return shownAmount(a[field], basis) === shownAmount(b[field], basis);
}

/** A stored percentage as it's shown: to 2 places (stored exactly, so switching back gives the same amount). */
export const shownPercent = (stored: string) => String(Math.round(toNumber(stored) * 100) / 100);

/**
 * A rule whose tax basis changes (an item's or group's tax), its money amounts (base price, $
 * markup) kept as they're shown: what was typed stays what it says, now read the new way ($100 inc
 * GST becomes $100 ex GST), so the amount before tax changes. The standard for every typed amount
 * when its tax is switched (QuotedAmount.tsx does the same for costs and supplier prices). A % has
 * no basis.
 */
export function keepShownAmounts(rule: PricingRule, from: Basis, to: Basis): PricingRule {
    const keep = (stored: string) => storedAmount(shownAmount(stored, from), to);
    return { ...rule, basePrice: keep(rule.basePrice), markupValue: rule.mode === "fixed" ? keep(rule.markupValue) : rule.markupValue };
}

/**
 * The base price that, with this rule's labour, gives `price` (no rounding): for keeping a price
 * someone typed in ServiceM8, while its labour still follows the labour rate.
 */
export function baseForPrice(
    item: { price_includes_taxes: boolean },
    tax: number,
    price: string,
    labourMinutes: number,
    settings: Pick<PriceListSettings, "labourRate" | "labourRateIncludesTax">,
) {
    const priceNet = toNet(toNumber(price), item.price_includes_taxes, tax);
    return toStored(priceNet - (labourRateNet(settings, tax) * labourMinutes) / 60);
}

/** What an item makes per unit, before tax (tax is never profit). */
export interface Profit {
    /** Its price less its cost. Null without a part cost (nothing to tell it from). */
    profitNet: number | null;
    /** That as a markup on the cost (%), or null. */
    markupPct: number | null;
    /** Its cost, before tax: the part's, and its labour's (with `partOnly`, none: the part's alone). */
    partNet: number;
    labourNet: number;
    /**
     * It has labour time but no labour cost is set, so its labour can't be costed: the part's alone (its price less its
     * labour as charged and the part's cost), as before labour was in costs.
     */
    partOnly: boolean;
}

/**
 * What an item's profit needs: its part's cost and labour (CostData), its cost to update if it has one, and the labour rate (for
 * the part's alone).
 */
export type ProfitData = CostData & Pick<PriceListData, "costUpdates"> & { settings: Pick<PriceListSettings, "labourCost" | "labourRate" | "labourRateIncludesTax"> };

/** An item's cost as it's estimated: the cost it'll have once its cost to update is done (supplierPrices.ts costUpdates), else ServiceM8's. */
export const estimatedCost = (data: Pick<PriceListData, "costUpdates">, item: { uuid: string; cost: string }) => data.costUpdates[item.uuid] ?? item.cost;

/**
 * What an item makes per unit, estimated: from its price, and its cost as it will be once any cost to update is done (estimatedCost:
 * its part's and its labour's as it costs now), so every figure counts its labour even before ServiceM8's cost has it. `was`:
 * `item` is as it will be (e.g. taxed another way by a price update, its price the one it'll have), and `was` as ServiceM8 has
 * it now: its cost (estimated the same way) is worked out from that, before tax, so it's the same estimate whichever way it's
 * taxed. Every "what it makes" uses this, so they all agree. See Profit.
 */
export function itemProfit(data: ProfitData, item: CostedItem & { price: string }, tax: number, opts: { was?: CostedItem } = {}): Profit {
    const minutes = data.itemPricing[item.uuid]?.labourMinutes ?? 0;
    const priceNet = toNet(toNumber(item.price), item.price_includes_taxes, tax);
    // Its cost, before tax: from ServiceM8's as it is now.
    const now = opts.was ?? item;
    const nowTax = opts.was ? fraction(rateFor(now.tax_rate_uuid, data.taxRates)) : tax;
    const net = (amount: string) => toNet(toNumber(amount), now.price_includes_taxes, nowTax);
    // Its cost to update has its labour as it costs now: the part's is the rest. Else the labour in ServiceM8's (up to date).
    const target = data.costUpdates[item.uuid];
    // Its labour can't be costed: the part's alone, its labour as charged left out (once its cost to update is done, none's in it;
    // until then, unless ServiceM8's has some).
    const partOnly = minutes > 0 && !hasLabourCost(data.settings) && (target !== undefined || !(toNumber(data.labourInCost[item.uuid] ?? "0") > 0));
    const labourNow = labourCostOf(data, item.uuid);
    let partNet = net(partItem(data, now).cost);
    let labourNet = Math.max(0, net(now.cost) - partNet);
    if (target !== undefined) {
        labourNet = labourNow;
        partNet = Math.max(0, net(target) - labourNow);
    }
    if (partOnly) labourNet = 0;
    if (partNet <= 0) return { profitNet: null, markupPct: null, partNet, labourNet, partOnly };
    const charged = partOnly ? (labourRateNet(data.settings, tax) * minutes) / 60 : 0;
    const costNet = partNet + labourNet;
    // To the cent, so float leftovers don't show as a loss of $0.00.
    const profitNet = roundCents(priceNet - charged - costNet) || 0;
    return { profitNet, markupPct: (profitNet / costNet) * 100, partNet, labourNet, partOnly };
}

/**
 * A kit's part cost (kits.ts), in its basis: each part's own cost (partItem: not the labour in it) before tax
 * (at its own rate) times how many, added up, then in the kit's basis. A part no longer there counts nothing.
 * No parts yet: nothing (its cost is then just its labour). Its ServiceM8 cost has its own labour in it too (costWithLabour).
 */
export function kitCost(
    kit: { price_includes_taxes: boolean; tax_rate_uuid: string },
    parts: readonly { partUuid: string; quantity: number }[],
    partOf: (uuid: string) => CostedItem | undefined,
    data: PartCostData,
): string {
    let net = 0;
    for (const p of parts) {
        const found = partOf(p.partUuid);
        const part = found && partItem(data, found);
        if (part) net += toNet(toNumber(part.cost), part.price_includes_taxes, fraction(rateFor(part.tax_rate_uuid, data.taxRates))) * p.quantity;
    }
    return toCents(fromNet(net, kit.price_includes_taxes, fraction(rateFor(kit.tax_rate_uuid, data.taxRates))));
}

/**
 * A kit's parts' own prices, before tax (each at its own rate), times how many, added up: what a kit priced from its parts' prices
 * starts from (stored exactly, like a rule's amounts). A part no longer there counts nothing. Null without parts.
 */
export function kitPartsPrice(
    parts: readonly { partUuid: string; quantity: number }[],
    partOf: (uuid: string) => { price: string; price_includes_taxes: boolean; tax_rate_uuid: string } | undefined,
    rates: readonly TaxRate[],
): string | null {
    if (!parts.length) return null;
    let net = 0;
    for (const p of parts) {
        const part = partOf(p.partUuid);
        if (part) net += toNet(toNumber(part.price), part.price_includes_taxes, fraction(rateFor(part.tax_rate_uuid, rates))) * p.quantity;
    }
    return toStored(net);
}

/** A markup under the minimum (Price List settings) is flagged; with no minimum, only making nothing or a loss. Null (no cost): never. */
export const underMinimum = (markupPct: number | null, minMarkup: number) => markupPct !== null && markupPct < Math.max(minMarkup, 0.0001);

/** ServiceM8 may round differently; closer than this counts as the same price. */
const SAME_PRICE = 0.006;
export const samePrice = (a: string | number, b: string | number) => Math.abs(toNumber(a) - toNumber(b)) < SAME_PRICE;

export interface NeedsCheck {
    /** The price its pricing gives now (in `taxTo`'s basis, when that's set). */
    target: string;
    /** "edited": changed in ServiceM8 (not what this app last saved); "changed": the cost, tax or labour rate moved. */
    reason: "edited" | "changed";
    /** It's in a group that taxes it otherwise: updating it also sets this. */
    taxTo: GroupTaxBasis | null;
}

/**
 * Whether an item's price needs checking: its pricing now gives a different price than it has, or
 * (in a group) the group taxes it otherwise. Prices never change by themselves; someone updates or
 * keeps each one.
 */
export function needsCheck(
    item: { cost: string; price: string; price_includes_taxes: boolean },
    rate: ItemRate,
    pricing: ItemPricing | undefined,
    settings: Pick<PriceListSettings, "labourRate" | "labourRateIncludesTax">,
): NeedsCheck | null {
    const priced = pricedAs(item, rate, pricing);
    const result = calcPrice(priced.item, priced.tax, pricing, settings);
    if (!result || !pricing || (!priced.taxTo && samePrice(result.price, item.price))) return null;
    const edited = pricing.appliedPrice !== null && !samePrice(pricing.appliedPrice, item.price);
    return { target: result.price, reason: edited ? "edited" : "changed", taxTo: priced.taxTo };
}
